import { useNextSanityImage } from "next-sanity-image"
import { type ReactNode, useState } from "react"
import { twMerge } from "tailwind-merge"
import {
  FullWidthLayout,
  type LayoutAlign,
  type LayoutSize,
} from "@/components/resources/components/layout/Layout"
import { Image, type ImageProps } from "@/components/resources/components/ui/Image"
import { getClient } from "@/components/resources/data/sanity/client"
import type { ContentImage } from "@/components/resources/data/sanity/posts"

interface LayoutSizes {
  wide: LayoutSize
  normal: LayoutSize
}

export type { LayoutSizes as SanityImageLayoutSizes }

type LayoutProps = {
  layoutSizes: LayoutSizes
  layoutAlign: LayoutAlign
}
interface Props extends Omit<ImageProps, "src" | "alt">, Partial<LayoutProps> {
  image: ContentImage
  alt?: string
}

export const SanityImage = ({ image, alt, ...props }: Props) => {
  const { src, ...imageProps } =
    useNextSanityImage(getClient(), image.asset ? image : "image-removed", {
      imageBuilder: (imageUrlBuilder, options) => {
        let urlBuilder = imageUrlBuilder.quality(80).dpr(2)

        if (options.width) {
          urlBuilder = urlBuilder.width(options.width)
        }

        return urlBuilder
      },
    }) ?? {}

  return (
    <Image
      alt={image.alt ?? image.caption ?? alt ?? ""}
      placeholder="blur"
      blurDataURL={image?.asset?.metadata?.lqip}
      src={src}
      sizes="100vw"
      {...imageProps}
      {...props}
    />
  )
}

const LightBoxImage = ({
  children,
  layoutSizes: { wide, normal },
}: {
  children: ReactNode
} & LayoutProps) => {
  const [enlarged, setEnlarged] = useState(false)

  const onToggleHandler = () => {
    setEnlarged((s) => !s)
  }
  return (
    <FullWidthLayout size={enlarged ? wide : normal} className="justify-center">
      <button
        type="button"
        onClick={onToggleHandler}
        className={twMerge("cursor-zoom-out", !enlarged && "cursor-zoom-in")}
      >
        {children}
      </button>
    </FullWidthLayout>
  )
}

const CaptionedFigure = ({ caption, children }: { caption: string; children: ReactNode }) => (
  <figure>
    {children}
    <figcaption className="text-sm text-fg-secondary pt-4 mt-1 px-3 text-center">
      {caption}
    </figcaption>
  </figure>
)

export const SanityContentImage = ({
  image,
  alt,
  layoutSizes = { wide: "site", normal: "wide-content" },
  layoutAlign = "center",
  ...props
}: Props) => {
  const { src, ...imageProps } =
    useNextSanityImage(getClient(), image.asset ? image : "image-removed", {
      imageBuilder: (imageUrlBuilder, options) => {
        let urlBuilder = imageUrlBuilder.quality(80).dpr(2)

        if (options.width) {
          urlBuilder = urlBuilder.width(options.width)
        }

        return urlBuilder
      },
    }) ?? {}

  let imageContent = <Image src={src} alt={image.alt ?? alt ?? ""} {...imageProps} {...props} />

  if (image.caption) {
    imageContent = <CaptionedFigure caption={image.caption}>{imageContent}</CaptionedFigure>
  }

  // If small image, return right away
  if (imageProps.width < 900) {
    return (
      <FullWidthLayout size={layoutSizes.normal} className="mb-12 py-6 justify-center">
        {imageContent}
      </FullWidthLayout>
    )
  }
  return (
    <LightBoxImage layoutSizes={layoutSizes} layoutAlign={layoutAlign}>
      {imageContent}
    </LightBoxImage>
  )
}
